import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PAGEI, PAGE_CONFIG, SideNavOptionsForPages, THEME, sideNavOptions } from '../interfaces/page-interface';
import { APIConfigMapping, HTTPAPIConfig, VlDataService, VlHttpService } from 'shared-library';
import { ActivatedRoute } from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';

@Injectable({
  providedIn: 'root'
})
export class WebBuilderService {
  userMetaInfo: any;

  constructor(
    private route: ActivatedRoute,
    private dataService: VlDataService,
    private httpService: VlHttpService
  ) {
    this.userMetaInfo = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo') as string) : null;
  }

  private selectedSidemenuOption = new BehaviorSubject<sideNavOptions>({ label: 'default', isActive: false });

  selectedOption$: Observable<sideNavOptions> = this.selectedSidemenuOption.asObservable();

  setSelectedOption(label: string, isActive: boolean) {
    const option: sideNavOptions = { label, isActive };
    this.selectedSidemenuOption.next(option);
  }

  private selectedPageSubject = new BehaviorSubject<PAGEI>({ pageId: '', pageName: '', applicationId: '' });

  selectedPageObj$: Observable<PAGEI> = this.selectedPageSubject.asObservable();

  setSelectedPage(data: PAGEI) {
    this.selectedPageSubject.next(data);
  }

  getSelectedPage() {
    return this.selectedPageSubject.value;
  }

  private sidenavContentlistSubject = new BehaviorSubject<SideNavOptionsForPages>({
    id: '',
    isActive: false
  });

  sidenavContentlistSubject$: Observable<SideNavOptionsForPages> = this.sidenavContentlistSubject.asObservable();

  setSidenavContentlistOption(id: string, isActive: boolean) {
    const option: SideNavOptionsForPages = { id, isActive };
    this.sidenavContentlistSubject.next(option);
  }

  getSidenavContentlist() {
    return this.sidenavContentlistSubject.value;
  }

  private WidgetListStatusSubject = new BehaviorSubject<boolean>(false);

  WidgetListStatus$: Observable<boolean> = this.WidgetListStatusSubject.asObservable();

  setWidgetListStatus(value: boolean) {
    this.WidgetListStatusSubject.next(value);
  }

  getSideNavStatus() {
    return this.WidgetListStatusSubject.value;
  }

  private themesSubject = new BehaviorSubject<THEME>({
    themeName: 'M3 Baseline Light',
    primaryColor: "#0061A6",
    secondaryColor: "#6C5778",
    defaultBackgroundColor: "#FAF9FD",
    defaultTextColor: "#45474A",
  })
  themesObj: Observable<THEME> = this.themesSubject.asObservable();

  private pagesListSubject = new BehaviorSubject<PAGEI[]>([]);
  pages$: Observable<PAGEI[]> = this.pagesListSubject.asObservable();

  // Pages Apis
  getAllPages(applicationId: string) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['GET_PAGES']);
    apiConfig.postData = { application_id: applicationId }
    this.httpService.placeAPICall(apiConfig).subscribe({
      next: (data: any) => {
        this.pagesListSubject.next(data);
      },
      error: (err: any) => {
        this.pagesListSubject.next([])
      }
    })
    return this.pages$;
  }

  getPageById(pageId: string): any {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['GET_PAGE']);
    apiConfig.postData = { page_id: pageId }
    return this.httpService.placeAPICall(apiConfig);
  }

  addPage(applicationId: string) {
    const page_config: PAGE_CONFIG = {
      style: { backgroundColor: this.themesSubject.value?.defaultBackgroundColor },
      actions: [],
      additionalConfig: {},
      pageForms: [],
      forms: []
    }
    const pagePostData: PAGEI = {
      applicationId: applicationId,
      pageName: `Page ${this.pagesListSubject.value.length + 1}`,
      pageConfig: page_config,
    }
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['CREATE_OR_UPDATE_PAGE']);
    apiConfig.postData = pagePostData
    this.httpService.placeAPICall(apiConfig).subscribe({
      next: (data: any) => {
        pagePostData.pageId = data.pageId;
        const updatedPages = [...this.pagesListSubject.value, pagePostData];
        this.pagesListSubject.next(updatedPages);
      },
      error: (data: any) => {

      }
    })
  }

  updatePage(pagePostData: any): any {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['CREATE_OR_UPDATE_PAGE']);
    apiConfig.postData = pagePostData;
    apiConfig.postData['userId'] = this.userMetaInfo ? this.userMetaInfo.userId : '';
    return new Observable((observer) => {
      this.httpService.placeAPICall(apiConfig).subscribe({
        next: (data: any) => {
          pagePostData.pageId = data?.pageId;
          let previousPagesData = this.pagesListSubject.value;
          previousPagesData = previousPagesData.map((page: PAGEI) => (page.pageId == pagePostData.pageId ? pagePostData : page));
          this.pagesListSubject.next(previousPagesData);
          observer.next(data);
          observer.complete();
        },
        error: (error: any) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  deletePage(pageId: string) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['DELETE_PAGE']);
    apiConfig.postData = { page_id: pageId }
    this.httpService.placeAPICall(apiConfig).subscribe({
      next: (data: any) => {
        const currentParams = { ...this.route.snapshot.queryParams };
        this.getAllPages(currentParams['id']);
      },
      error: (data: any) => {

      }
    })
  }

  //end pages apis

  //forms apis

  createForm(formPostData: any): any {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['CREATE_OR_UPDATE_FORM']);
    apiConfig.postData = formPostData;
    apiConfig.postData['createdBy'] = this.userMetaInfo?.userId
    return this.httpService.placeAPICall(apiConfig)
  }

  updateForm(formPostData: any): any {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['CREATE_OR_UPDATE_FORM']);
    apiConfig.postData = formPostData;
    apiConfig.postData['userId'] = this.userMetaInfo?.userId;
    return this.httpService.placeAPICall(apiConfig);
  }

  //end form apis

  //widget apis

  createWidget(widgetPostData: any): any {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['CREATE_OR_EDIT_WIDGET']);
    apiConfig.postData = widgetPostData;
    return this.httpService.placeAPICall(apiConfig);
  }

  updateWidget(widgetPostData: any): any {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['CREATE_OR_EDIT_WIDGET']);
    apiConfig.postData = widgetPostData;
    return this.httpService.placeAPICall(apiConfig);
  }

  deleteWidget(widgetId: any) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['DELETE_WIDGET']);
    apiConfig.postData = { widget_id: widgetId };

    this.httpService.placeAPICall(apiConfig).subscribe({
      next: (data: any) => {
      },
      error: (data: any) => {

      }
    })
  }

  //end widget apis

  widgetRearrange(postData: any) {
    const apiConfig: HTTPAPIConfig = this.dataService.deepClone(APIConfigMapping['WIDGET_REARRANGE']);
    apiConfig.postData = postData;
    apiConfig.postData['userId'] = this.userMetaInfo ? this.userMetaInfo.userId : '';
    return new Observable((observer) => {
      this.httpService.placeAPICall(apiConfig).subscribe({
        next: (data: any) => {
          observer.next(data);
          observer.complete();
        },
        error: (error: any) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  private sidenav!: MatSidenav;
  // private mainNav:boolean = false;

  public mainNav = new BehaviorSubject<boolean>(false);

  public mainNav$: Observable<boolean> = this.mainNav.asObservable();

  public enableMainNav(){
   this.mainNav.next(true);
  }

  public disableMainNav(){
    this.mainNav.next(false);
   }

  public resetMainAndNavStatus(){
    this.mainNav.next(false);
    this.sidenav.close();
  }

	public setSidenav(sidenav: MatSidenav) {
		this.sidenav = sidenav;
	}

	public open() {
		return this.sidenav.open();
	}


	public closeDrawer() {
		return this.sidenav.close();
	}

	public drawerToggle(): void {
		this.sidenav.toggle();
	}
}
